/* ============================================================================
   FIXED-SCHEDULE TRACKS — two clocks, and neither is a score  (SITE-RULES 3b)

     LEAD TRACK    day 0 = the enquiry lands. Ends at conversion or at day 45.
     MEMBER TRACK  day 0 = the join date. Runs the first 90 days.

   A member who converts INHERITS their anchor but starts the member clock at ZERO
   (owner ruling, 2026-09-03). The relationship carries across; the clock does not. Do
   not collapse those into one thing.

   WHY A SCHEDULE AND NOT A SCORE. Both populations have no baseline yet. Drift is
   measured against a member's OWN established rhythm, and somebody in their first weeks
   has not established one, so a risk score computed for them is a number about nothing.
   They run a fixed schedule instead and join the risk model at day 90, when there is
   finally a baseline to drift from. Same reasoning as suppression: the model declines to
   score what it cannot yet see.

   THESE ARE CONTACT CADENCES, NOT EVENT LOGS. A checkpoint is a scheduled touch — a
   call, a message, a conversation — that a named person owes. Trials, visits and
   payments are EVENTS that happen inside the cadence and count as evidence it was met.
   ============================================================================ */
import { parseDay, todayISO } from './time.js';
/**
 * Cadence set by the owner, 2026-09-03: leads run Hr 1 / Day 2 / Day 3 / Day 7 and out.
 *
 * TRIAL ONE AND TRIAL TWO ARE 2–3 DAYS APART. That number comes from FNS and the Lab and
 * it is the one MEASURED value in the lead track; everything else hangs off it. It is
 * why the close sits at day 7 and not day 21: a lead who has done both trials and has
 * not been asked is a lead deciding on their own, and the ask has to land while the
 * second session is still in their body. Day 2 and day 7 are still inferred — if the
 * real enquiry-to-first-trial gap differs, THIS is the only place to change it.
 */
export const LEAD_TRACK = [
    /* Labelled '1', not 'Hr 1'. A four-character pill among one- and two-character ones
       makes the strip ragged; the tooltip and the `what` line still say it is the first hour. */
    { at: 0.04, label: '1', what: 'Within the hour: first response, from a named person' },
    { at: 2, label: 'Day 2', what: 'Book the first trial' },
    { at: 3, label: 'Day 3', what: 'After trial one: book trial two' },
    { at: 7, label: 'Day 7', what: 'Membership conversation, both trials done' },
    { at: 14, label: 'Day 14', what: 'Second ask, or move them to nurture' },
    { at: 21, label: 'Day 21', what: 'Nurture cadence begins' },
    { at: 45, label: 'Day 45', what: 'Close it cleanly rather than letting it drift' },
];
/**
 * A lost lead is not a dead lead — it is a person who said no once. These go out
 * automatically and each one carries a REASON rather than a discount, from their
 * original anchor where one exists: the win-back should come from the person they
 * actually met, not from the club.
 */
export const LOST_LEAD_TRACK = [
    { at: 7, label: 'Day 7', what: 'Thanks, and the door stays open' },
    { at: 30, label: 'Day 30', what: 'Something specific has changed here since they left' },
    { at: 90, label: 'Day 90', what: 'The human one, from their original anchor' },
];
/**
 * First 90 days: a messaging TIMELINE, dense at the start and spacing out as a rhythm
 * forms. After day 90 there is no timeline — contact becomes event-driven only: a
 * milestone, a need, a missed class, a payment problem. That is the handoff to the risk
 * model and the move it asks for, and it is deliberate. A member with an established
 * rhythm does not need a schedule; they need somebody to notice when the rhythm breaks.
 */
export const MEMBER_TRACK = [
    // Week 1 — near-daily. This is where the habit is won or lost.
    { at: 1, label: 'Day 1', what: 'Personal welcome, a tour, and two introductions: one to a named member of staff, one to another member', milestone: true, owner: 'sales' },
    { at: 2, label: '2', what: 'Did they book a second session' },
    { at: 3, label: '3', what: 'After session two: how did it feel' },
    { at: 5, label: '5', what: 'Nudge if nothing is on the calendar' },
    { at: 7, label: 'Day 7', what: 'Check the first week actually happened. If there are no visits, call. Do not email', milestone: true, owner: 'sales' },
    // Weeks 2-4 — every 2-3 days, easing off as a pattern appears.
    { at: 10, label: '10', what: 'Check the time of day is sticking' },
    { at: 12, label: '12', what: 'Introduce them to one other member' },
    { at: 14, label: '14', what: 'Two weeks in: first honest ask about the plan' },
    { at: 17, label: '17', what: 'Class variety: have they tried a second format' },
    { at: 21, label: 'Day 21', what: 'Programme review, and deliberate anchor placement into a class or a cohort', milestone: true, owner: 'coach' },
    { at: 24, label: '24', what: 'Name-check from a coach on the floor' },
    { at: 28, label: '28', what: 'Four weeks: any obstacle worth removing' },
    // Month 2 — weekly-ish.
    { at: 31, label: '31', what: 'One month: first progress conversation' },
    { at: 35, label: '35', what: 'Check in on the goal they came in for' },
    { at: 38, label: '38', what: 'Invite to something outside their usual slot' },
    { at: 42, label: '42', what: 'Quiet week check' },
    { at: 45, label: 'Day 45', what: 'How is it fitting into your week? A scheduling conversation, not a sales one', milestone: true, owner: 'manager' },
    { at: 49, label: '49', what: 'Is the second relationship real yet' },
    { at: 52, label: '52', what: 'Small win worth naming' },
    { at: 59, label: '59', what: 'Two months: recalibrate the goal' },
    // Month 3 — weekly, winding down as the baseline forms.
    { at: 66, label: '66', what: 'Steady-state check' },
    { at: 73, label: '73', what: 'Anything changed in their week' },
    { at: 80, label: '80', what: 'Pre-90 read: are they a member or a visitor' },
    { at: 90, label: 'Day 90', what: 'The anchor audit. Count the named relationships: fewer than two is a formal intervention', milestone: true, owner: 'gym_admin' },
];
/**
 * Whole days from `from` to `to`. Both ends anchored at 12:00 local so DST cannot shift
 * the answer. Null when either end is missing or unparseable.
 */
export function daysBetween(from, to) {
    if (!from)
        return null;
    const a = parseDay(from);
    const b = to instanceof Date ? to.getTime() : parseDay(to);
    if (a == null || b == null)
        return null;
    return Math.floor((b - a) / 86400000);
}
/**
 * A CHECKPOINT IS DONE ONLY ON EVIDENCE — a visit, a logged touch, a stage move. Marking
 * one done because its date passed is how a schedule becomes a decoration.
 *
 * And a checkpoint is only ASSESSABLE once its date has passed. Without the `reached`
 * guard, evidence falling in an open-ended future window marked later checkpoints done:
 * a day-47 member showed Day 45 AND Day 90 as met, which is impossible and makes the
 * whole strip a lie. Future checkpoints are simply upcoming.
 */
export function buildTrack(track, dayZero, evidenceAt = [], today = todayISO()) {
    const day = daysBetween(dayZero, today) ?? 0;
    const ev = evidenceAt
        .map(d => daysBetween(dayZero, d))
        .filter((n) => n != null && n >= 0);
    const steps = track.map((cp, i) => {
        const prev = i ? track[i - 1].at : -1;
        const reached = day >= cp.at;
        const done = reached && ev.some(n => n > prev && n <= cp.at + 0.999);
        return { ...cp, done, overdue: reached && !done, upcoming: !reached, current: false };
    });
    /* The next thing to DO is the soonest checkpoint still ahead. Missed ones are reported
       as a COUNT, not as "next": you cannot go back and do Day 1 on day 47. */
    const next = steps.find(x => x.upcoming) ?? null;
    if (next)
        next.current = true;
    const missed = steps.filter(x => x.overdue);
    const last = track[track.length - 1];
    return {
        day,
        steps,
        nextStep: next,
        overdue: missed.length,
        missed,
        complete: !next && missed.length === 0,
        pct: last ? Math.max(0, Math.min(100, Math.round((day / last.at) * 100))) : 0,
    };
}
/* ============================================================================
   THE FIRST 90 DAYS, AS A THING SOMEBODY RUNS                       (PRD 20)

   The track above is the cadence. This is the accountability over it: which of
   the five owed touches have happened, who owes the next one, and whether this
   member is on track, worth watching, overdue, or needs a formal intervention.

   WHY IT HAS TO BE VISIBLE. "Scoring is suppressed for the whole 90 days. The
   track replaces it, which is why the track has to be visible: a member with
   nothing on it is invisible in both systems at once." That sentence is the
   whole argument for this screen existing. A new member is the most fragile
   person in the building and the risk model deliberately says nothing about
   them, so if the track is not being read they are being watched by nobody.
   ============================================================================ */
/** Fewer than this at day 90 and later outreach does not reliably reverse it. */
export const ANCHOR_AUDIT_TARGET = 2;
/**
 * THE DAY-90 ANCHOR AUDIT, which PRD 20 calls the highest-leverage single moment
 * in the programme.
 *
 * "A member reaching it with two or more anchors is substantially self-sustaining.
 * A member reaching it with none is on a countdown that later outreach does not
 * reliably reverse." So the audit is not a touch that can be marked done: it is a
 * COUNT, and the count either cleared the bar or it did not. Recording that the
 * conversation happened while the member still has no anchors would be the exact
 * failure this moment exists to catch.
 */
export function anchorAudit(anchors) {
    const n = Array.isArray(anchors) ? anchors.length : 0;
    return { anchors: n, passed: n >= ANCHOR_AUDIT_TARGET };
}
export function firstNinety(m, evidenceAt = [], today = todayISO()) {
    const built = buildTrack(MEMBER_TRACK, m.joinedAt, evidenceAt, today);
    const touches = built.steps.filter((s) => s.owner);
    const missed = touches.filter((s) => s.overdue);
    const next = touches.find((s) => s.upcoming) ?? null;
    const reached90 = built.day >= 90;
    const audit = reached90 ? anchorAudit(m.anchors) : null;
    /* THE ORDER OF THESE IS THE RULE. A failed audit outranks everything, because
       it is the one finding that later outreach does not reliably reverse and it is
       no longer fixable by catching up on a touch. */
    let state;
    let because;
    if (audit && !audit.passed) {
        state = 'intervention';
        because = audit.anchors === 0
            ? 'Day 90 and nobody here knows them. This is the formal intervention.'
            : `Day 90 with one anchor, and two is the bar. This is the formal intervention.`;
    }
    else if (audit) {
        state = 'on track';
        because = `Day 90 with ${audit.anchors} anchors: substantially self-sustaining.`;
    }
    else if (missed.length >= 2) {
        state = 'overdue';
        because = `${missed.length} of the five owed touches have not happened.`;
    }
    else if (missed.length === 1) {
        state = 'watch';
        because = `${missed[0].label} has not happened.`;
    }
    else {
        state = 'on track';
        because = next ? `Next is ${next.label}.` : 'Every owed touch has happened.';
    }
    return {
        day: built.day,
        touches,
        next,
        owedBy: next?.owner ?? null,
        missed,
        missedBy: missed[0]?.owner ?? null,
        state,
        audit,
        because,
    };
}
